import { useCallback, useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import partialRight from 'lodash/partialRight';
import type { EditorState } from '../components/Editor/types';
import { EditorContext } from '../components/Editor/Context';

import {
  saveEditorDraft as saveEditorDraftAction,
  removeEditorDraft as removeEditorDraftAction,
} from './actions';
import { getEditorDraft } from './selectors';

/** Returns the draft state for the given `editorId` */
export function useEditorDraftState(editorId) {
  return useSelector(partialRight(getEditorDraft, editorId));
}

/** Returns functions that help manage editor drafts.  */
export function useEditorDraftActions(editorId: string) {
  const draftValue = useEditorDraftState(editorId);
  const dispatch = useDispatch();

  const saveEditorDraft = useCallback(
    (state: EditorState) => {
      dispatch(saveEditorDraftAction(editorId, state));
    },
    [dispatch, editorId]
  );

  const removeEditorDraft = useCallback(() => {
    if (draftValue) {
      dispatch(removeEditorDraftAction(editorId));
    }
  }, [dispatch, editorId, draftValue]);

  return {
    /**
     * Saves the editor draft to redux and local storage.
     */
    saveEditorDraft,

    /**
     * Removes the editor draft from redux and local storage.
     */
    removeEditorDraft,
  };
}

/**
 * Makes sure our draft states are updated before a window unloads
 *
 * Relies on our {@link EditorContext}.
 */
export function useEditorDraftUnmountEffects(editorId: string) {
  const { isReady, getStateForSave, isEmpty } = useContext(EditorContext);
  const { saveEditorDraft, removeEditorDraft } =
    useEditorDraftActions(editorId);

  const handleBeforeUnload = useCallback(() => {
    if (!isEmpty && isReady) {
      saveEditorDraft(getStateForSave());
    } else if (isReady) {
      removeEditorDraft();
    }
  }, [getStateForSave, saveEditorDraft, removeEditorDraft, isReady, isEmpty]);

  // beforeunload instead of purely doing on unmount so
  // that page navigation also triggers a draft removal/save
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    };
  }, [handleBeforeUnload]);
}
